import { Fragment } from 'react'
import { formatRevenueFull } from './formatters'
import { type BreadcrumbCrumb } from './PlanNodesPanel'

// The "Top level › Group › Sub-group" trail above the node grid, with every
// crumb but the last clickable to navigate back up.
export function Breadcrumb({
  crumbs,
  onNavigate
}: {
  crumbs: BreadcrumbCrumb[]
  onNavigate: (id: string | null) => void
}) {
  return (
    <div className="text-xs flex items-center flex-wrap gap-x-1" style={{ color: 'var(--muted-foreground)' }}>
      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1
        return (
          <Fragment key={`${c.id ?? 'root'}-${i}`}>
            {isLast ? (
              <span style={{ color: 'var(--foreground)', fontWeight: 500 }}>{c.label}</span>
            ) : (
              <button
                type="button"
                onClick={() => onNavigate(c.id)}
                className="hover:underline"
                style={{ color: 'var(--link)' }}
              >
                {c.label}
              </button>
            )}
            {!isLast && <span>›</span>}
          </Fragment>
        )
      })}
    </div>
  )
}

// Target vs. allocated-to-children summary shown while drilled into a node.
export function ParentSummary({
  parentTarget,
  childrenSum,
  remaining,
  currency
}: {
  parentTarget: number
  childrenSum: number
  remaining: number
  currency?: string | null
  hasSpecial: boolean
}) {
  const overshoot = remaining < 0
  const balanced = remaining === 0 && parentTarget > 0
  const deltaColor = balanced
    ? 'var(--success, var(--foreground))'
    : 'var(--destructive, #c53030)'
  const deltaLabel = overshoot
    ? `${formatRevenueFull(Math.abs(remaining), currency)} over`
    : balanced
      ? 'Balanced'
      : `${formatRevenueFull(remaining, currency)} remaining`

  return (
    <div
      className="px-4 py-2 border-b flex items-center gap-4 text-xs"
      style={{ borderColor: 'var(--border)', background: 'var(--accent, transparent)' }}
    >
      <div className="flex items-baseline gap-1.5">
        <span style={{ color: 'var(--muted-foreground)' }}>Target</span>
        <span className="tabular-nums" style={{ color: 'var(--foreground)', fontWeight: 600 }}>
          {formatRevenueFull(parentTarget, currency)}
        </span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span style={{ color: 'var(--muted-foreground)' }}>Allocated</span>
        <span className="tabular-nums" style={{ color: 'var(--foreground)', fontWeight: 600 }}>
          {formatRevenueFull(childrenSum, currency)}
        </span>
        <span className="tabular-nums" style={{ color: deltaColor, fontWeight: 600 }}>
          {deltaLabel}
        </span>
      </div>
    </div>
  )
}
